import clsx from 'clsx';
import { ClockIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { LuUmbrella } from 'react-icons/lu';
import { PROJECTED_DEADLINE_HINT } from '../../../vacations.definitions';
import type { VacationRequest } from '../../../models/vacation.types';
import {
    FACT_LABELS,
    FACT_TONE_CLASSES,
    getSellTag,
    getDeadlineTag,
} from '../vacationRequestSummary.definitions';

interface SummaryFactsProps {
    modalityLabel: string;
    sellRequest: VacationRequest['sellRequest'];
    deadline?: string | null;
    deadlineRaw?: Date | string | null;
    showProjectedHint: boolean;
}

function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

interface FactItemProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    tag?: { label: string; tone: 'success' | 'warning' | 'danger' | 'muted' } | null;
    hint?: string | null;
}

function FactItem({ icon, label, value, tag, hint }: FactItemProps) {
    return (
        <div className="flex flex-1 items-start gap-3 py-3 px-4">
            <div className="text-raro-gray-500 shrink-0 mt-0.5">{icon}</div>
            <div className="min-w-0 flex-1">
                <p className="text-xs text-raro-gray-500 font-montserrat">{label}</p>
                <p className="text-sm font-semibold text-raro-gray-900 font-montserrat">{value}</p>
                {hint && <p className="mt-1 text-xs text-raro-gray-500 font-montserrat">{hint}</p>}
            </div>
            {tag && (
                <span
                    className={clsx(
                        'shrink-0 rounded-full px-2.5 py-1 text-xs font-medium font-montserrat',
                        FACT_TONE_CLASSES[tag.tone]
                    )}
                >
                    {capitalize(tag.label)}
                </span>
            )}
        </div>
    );
}

export function SummaryFacts({
    modalityLabel,
    sellRequest,
    deadline,
    deadlineRaw,
    showProjectedHint,
}: SummaryFactsProps) {
    const sellTag = sellRequest.requested ? getSellTag(sellRequest.status) : null;
    const sellValue = sellRequest.requested ? `${sellRequest.days} dias vendidos` : null;
    const deadlineTag = deadline ? getDeadlineTag(deadlineRaw || deadline) : null;

    return (
        <div className="rounded-xl bg-raro-surface-50 divide-y divide-raro-gray-100 sm:divide-y-0 sm:divide-x sm:flex">
            <FactItem
                icon={<LuUmbrella className="h-5 w-5" />}
                label={FACT_LABELS.vacation}
                value={modalityLabel}
            />
            {deadline && (
                <FactItem
                    icon={<ClockIcon className="h-5 w-5" />}
                    label={FACT_LABELS.deadline}
                    value={`até ${deadline}`}
                    tag={deadlineTag}
                    hint={showProjectedHint ? PROJECTED_DEADLINE_HINT : null}
                />
            )}
            {sellValue && (
                <FactItem
                    icon={<BanknotesIcon className="h-5 w-5" />}
                    label={FACT_LABELS.sell}
                    value={sellValue}
                    tag={sellTag}
                />
            )}
        </div>
    );
}

export default SummaryFacts;
